import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { Plus, Search, Edit, Trash2, X, Package, Copy, Check, Tv2 } from 'lucide-react'

interface User {
  id: string
  username: string
  max_connections: number
  current_connections: number
  is_active: boolean
  expires_at?: string
  bouquet_ids?: string[]
  web_player_enabled?: boolean
  created_at: string
}

interface Bouquet {
  id: string
  name: string
  is_active: boolean
}

interface UserFormData {
  username: string
  password: string
  max_connections: number
  is_active: boolean
  expires_at: string
  bouquet_ids: string[]
  web_player_enabled: boolean
}

const defaultFormData: UserFormData = {
  username: '',
  password: '',
  max_connections: 1,
  is_active: true,
  expires_at: '',
  bouquet_ids: [],
  web_player_enabled: false,
}

export default function Users() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState<UserFormData>(defaultFormData)
  const [error, setError] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: () => api.getUsers({ page, per_page: 20, search }),
    refetchInterval: 5000, // Auto-refresh every 5 seconds for live connection counts
  })

  // Fetch all bouquets for selection
  const { data: bouquetsData } = useQuery({
    queryKey: ['bouquets-all'],
    queryFn: () => api.getBouquets({ per_page: 1000 }),
  })

  const createMutation = useMutation({
    mutationFn: (data: UserFormData) => api.createUser({
      username: data.username,
      password: data.password,
      max_connections: data.max_connections,
      is_active: data.is_active,
      expires_at: data.expires_at || undefined,
      bouquet_ids: data.bouquet_ids,
      web_player_enabled: data.web_player_enabled,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create user')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserFormData> }) =>
      api.updateUser(id, {
        password: data.password || undefined,
        max_connections: data.max_connections,
        is_active: data.is_active,
        expires_at: data.expires_at || undefined,
        bouquet_ids: data.bouquet_ids,
        web_player_enabled: data.web_player_enabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      closeModal()
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update user')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const openCreateModal = () => {
    setEditingUser(null)
    setFormData(defaultFormData)
    setError('')
    setModalOpen(true)
  }

  const openEditModal = (user: User) => {
    setEditingUser(user)
    setFormData({
      username: user.username,
      password: '',
      max_connections: user.max_connections,
      is_active: user.is_active,
      expires_at: user.expires_at ? user.expires_at.split('T')[0] : '',
      bouquet_ids: user.bouquet_ids || [],
      web_player_enabled: user.web_player_enabled || false,
    })
    setError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingUser(null)
    setFormData(defaultFormData)
    setError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: formData })
    } else {
      if (!formData.password) {
        setError('Password is required')
        return
      }
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (user: User) => {
    if (confirm(`Delete user "${user.username}"?`)) {
      deleteMutation.mutate(user.id)
    }
  }

  const toggleBouquet = (bouquetId: string) => {
    setFormData(prev => ({
      ...prev,
      bouquet_ids: prev.bouquet_ids.includes(bouquetId)
        ? prev.bouquet_ids.filter(id => id !== bouquetId)
        : [...prev.bouquet_ids, bouquetId]
    }))
  }

  const copyPlaylistUrl = async (user: User) => {
    const baseUrl = window.location.origin
    const url = `${baseUrl}/playlist/${user.username}/PASSWORD.m3u`

    try {
      // Use clipboard API if available (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url)
      } else {
        // Fallback for HTTP connections
        const textArea = document.createElement('textarea')
        textArea.value = url
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setCopiedId(user.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const users = data?.data || []
  const total = data?.total || 0
  const totalPages = data?.total_pages || 1
  const bouquets: Bouquet[] = bouquetsData?.bouquets || []

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">Users</h1>
        <button onClick={openCreateModal} className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add User</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-12"
        />
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Username</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Connections</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Bouquets</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Expires</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : users.length > 0 ? (
              users.map((user: User) => (
                <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-medium text-foreground">{user.username}</span>
                      {user.web_player_enabled && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-500 text-xs rounded" title="Web Player Enabled">
                          <Tv2 className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={user.is_active ? 'badge-success' : 'badge-destructive'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-base ${user.current_connections >= user.max_connections ? 'text-warning font-medium' : 'text-muted-foreground'}`}>
                      {user.current_connections} / {user.max_connections}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Package className="w-4 h-4" />
                      <span className="text-base">{user.bouquet_ids?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-base text-muted-foreground">
                    {user.expires_at ? formatDate(user.expires_at) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => copyPlaylistUrl(user)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        title="Copy M3U playlist URL"
                      >
                        {copiedId === user.id ? (
                          <Check className="w-4 h-4 text-success" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-base text-muted-foreground">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm sm:text-base text-muted-foreground">
            Showing {users.length} of {total} users
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-card rounded-lg border border-border p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">
                {editingUser ? 'Edit User' : 'Add User'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-base">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="input-field"
                  required
                  disabled={!!editingUser}
                />
              </div>

              <div>
                <label className="form-label">
                  Password {editingUser && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-field"
                  required={!editingUser}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Max Connections</label>
                  <input
                    type="number"
                    value={formData.max_connections}
                    onChange={(e) => setFormData({ ...formData, max_connections: parseInt(e.target.value) || 1 })}
                    className="input-field"
                    min="1"
                  />
                </div>

                <div>
                  <label className="form-label">Expires At</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>

              {/* Bouquets Selection */}
              <div>
                <label className="form-label">
                  Bouquets ({formData.bouquet_ids.length} selected)
                  <span className="text-muted-foreground font-normal ml-2">- Determines which streams user can access</span>
                </label>
                <div className="border border-border rounded-lg p-3 max-h-40 overflow-y-auto bg-background">
                  {bouquets.length > 0 ? (
                    <div className="space-y-1">
                      {bouquets.map((bouquet: Bouquet) => (
                        <label
                          key={bouquet.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.bouquet_ids.includes(bouquet.id)}
                            onChange={() => toggleBouquet(bouquet.id)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-foreground">{bouquet.name}</span>
                          {!bouquet.is_active && (
                            <span className="text-xs text-muted-foreground">(inactive)</span>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">No bouquets available. Create a bouquet first.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_active" className="text-base text-foreground">Active</label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="web_player_enabled"
                    checked={formData.web_player_enabled}
                    onChange={(e) => setFormData({ ...formData, web_player_enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="web_player_enabled" className="text-base text-foreground flex items-center gap-2">
                    <Tv2 className="w-4 h-4" />
                    Enable Web Player Access
                  </label>
                </div>
              </div>

              {/* M3U URL Info */}
              {editingUser && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-2">M3U Playlist URL:</p>
                  <code className="text-xs text-foreground break-all">
                    {window.location.origin}/playlist/{editingUser.username}/PASSWORD.m3u
                  </code>
                  <p className="text-xs text-muted-foreground mt-2">Replace PASSWORD with the user's actual password</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingUser
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
